import { useCallback, useRef, useState } from 'react';

import { getTextEditorPlainTextValue, TextEditorValue } from '@dreryk/react-text-editor';

import { useElevenLabsTranscription } from '../transcription/useElevenLabsTranscription';
import { useAiAssistantProviderGlobalState } from '../shared/useAiAssistantProviderGlobalState';
import { useAiAssistantTokensQuotaGuard } from '../shared/useAiAssistantTokensQuotaGuard';

import { TextEditorFieldAccess } from './textEditorField.types';
import { buildLivePreviewHtml, escapeHtml } from './textEditorField.utils';

export type UseTextEditorFieldDictationResult = {
   handleBeforeStart: () => Promise<boolean>;
   handleStart: () => Promise<boolean>;
   handleStop: VoidFunction;
   isRecording: boolean;
   isConnecting: boolean;
   isDictating: boolean;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const useTextEditorFieldDictation = (access: TextEditorFieldAccess): UseTextEditorFieldDictationResult => {
   const [isDictating, setIsDictating] = useState(false);
   const accumulatedTextRef = useRef('');
   const partialTextRef = useRef('');
   const originalHtmlRef = useRef('');
   const accessRef = useRef(access);
   accessRef.current = access;
   const startFailedRef = useRef(false);
   const handleBeforeStart = useAiAssistantTokensQuotaGuard();

   const updateLivePreview = useCallback(() => {
      accessRef.current.setHtml(
         buildLivePreviewHtml(originalHtmlRef.current, accumulatedTextRef.current, partialTextRef.current)
      );
   }, []);

   const handlePartial = useCallback(
      (text: string) => {
         partialTextRef.current = text;
         updateLivePreview();
      },
      [updateLivePreview]
   );

   const handleFinal = useCallback(
      (text: string) => {
         accumulatedTextRef.current = `${accumulatedTextRef.current}${text} `;
         partialTextRef.current = '';
         updateLivePreview();
      },
      [updateLivePreview]
   );

   const handleTranscriptionStop = useCallback(() => {
      const pending = partialTextRef.current.trim();

      if (pending.length > 0) {
         accumulatedTextRef.current = `${accumulatedTextRef.current}${pending} `;
      }

      const text = accumulatedTextRef.current.trim();
      const originalHtml = originalHtmlRef.current;

      accessRef.current.setHtml(text ? `${originalHtml}<p>${escapeHtml(text)}</p>` : originalHtml);

      accumulatedTextRef.current = '';
      partialTextRef.current = '';
      originalHtmlRef.current = '';
      setIsDictating(false);
   }, []);

   const { dictationConfig } = useAiAssistantProviderGlobalState();

   const options = {
      onPartial: handlePartial,
      onFinal: handleFinal,
      onStop: handleTranscriptionStop,
      onError: () => {
         startFailedRef.current = true;
         handleTranscriptionStop();
      },
   };
   const transcription = useElevenLabsTranscription(options);

   const handleStart = useCallback(async () => {
      const html = accessRef.current.getHtml();
      const hasText = getTextEditorPlainTextValue(TextEditorValue.fromFormatted(html)).trim().length > 0;

      originalHtmlRef.current = hasText ? html : '';
      accumulatedTextRef.current = '';
      partialTextRef.current = '';

      startFailedRef.current = false;
      setIsDictating(true);

      await transcription.start(false, dictationConfig);

      return !startFailedRef.current;
   }, [transcription, dictationConfig]);

   const handleStop = useCallback(() => {
      transcription.stop();
   }, [transcription]);

   return {
      handleBeforeStart: handleBeforeStart,
      handleStart: handleStart,
      handleStop: handleStop,
      isRecording: transcription.state === 'recording',
      isConnecting: transcription.state === 'requesting-credentials' || transcription.state === 'connecting',
      isDictating: isDictating,
   };
};
